import { motion } from 'motion/react';
import { Card } from './ui/card';
import { LucideIcon } from 'lucide-react';

interface WeatherMetricCardProps {
  title: string;
  value: string | number;
  unit: string;
  icon: LucideIcon;
  trend?: 'up' | 'down' | 'stable';
  color: string;
  delay?: number;
}

export function WeatherMetricCard({
  title,
  value,
  unit,
  icon: Icon,
  trend,
  color,
  delay = 0,
}: WeatherMetricCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ scale: 1.05 }}
    >
      <Card className="p-6 relative overflow-hidden">
        <motion.div
          className={`absolute top-0 right-0 w-32 h-32 ${color} opacity-10 rounded-full -mr-16 -mt-16`}
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">{title}</span>
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <Icon className={`w-5 h-5 ${color.replace('bg-', 'text-')}`} />
            </motion.div>
          </div>
          
          <div className="flex items-baseline gap-1">
            <motion.span
              className="text-3xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: delay + 0.2 }}
            >
              {typeof value === 'number' ? value.toFixed(2) : value}
            </motion.span>
            <span className="text-gray-500">{unit}</span>
          </div>
          
          {trend && (
            <motion.div
              className="mt-2 flex items-center gap-1 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: delay + 0.4 }}
            >
              {trend === 'up' && <span className="text-red-500">↑ Wzrost</span>}
              {trend === 'down' && <span className="text-blue-500">↓ Spadek</span>}
              {trend === 'stable' && <span className="text-gray-500">→ Stabilnie</span>}
            </motion.div>
          )}
        </div>
      </Card>
    </motion.div>
  );
}
